/** Phase 5 reads and writes (PHASE-5 §5.5).
 *
 * Every shape here comes from the route contract, so a screen cannot drift from
 * what the Worker actually answers. Mutations invalidate the queue and Today
 * together: releasing a message changes both, and a badge that lags is worse than
 * no badge.
 */
import { queryOptions } from "@tanstack/react-query";
import type { InferResponseType } from "hono/client";
import { readJson, rpc, type JsonBody, type QueryParams } from "./api";
import { queryClient } from "./session";

export type QueueItem = InferResponseType<typeof rpc.api.queue.$get>["items"][number];
export type QueueFilters = QueryParams<typeof rpc.api.queue.$get>;
export type MessageTemplateRow = InferResponseType<typeof rpc.api["message-templates"]["$get"]>["items"][number];
export type TemplatePreview = InferResponseType<typeof rpc.api["message-templates"][":id"]["preview"]["$get"]>;
export type DripSequence = InferResponseType<typeof rpc.api.drips.$get>["sequences"][number];
export type AutomationSettings = InferResponseType<typeof rpc.api.settings.automation.$get>;
export type CalendarConnection = InferResponseType<typeof rpc.api.settings.calendar.$get>;
export type BookingVendor = InferResponseType<typeof rpc.api.bookings[":id"]["vendors"]["$get"]>["items"][number];

// ---------------------------------------------------------------- reads

export const queueQuery = (filters: QueueFilters = {}) => queryOptions({
  queryKey: ["queue", filters],
  queryFn: () => readJson(rpc.api.queue.$get({ query: filters })),
  staleTime: 15_000,
  refetchOnWindowFocus: true,
});

/** Just the number, for the Today tab badge. Cheap enough to keep fresh. */
export const queueCountQuery = queryOptions({
  queryKey: ["queue-count"],
  queryFn: () => readJson(rpc.api.queue.count.$get()).then(result => result.count),
  staleTime: 30_000,
  refetchOnWindowFocus: true,
});

export const messageTemplatesQuery = queryOptions({
  queryKey: ["message-templates"],
  queryFn: () => readJson(rpc.api["message-templates"].$get()).then(result => result.items),
  staleTime: 5 * 60_000,
});

export const templatePreviewQuery = (id: string, channel: "sms" | "email") => queryOptions({
  queryKey: ["message-template-preview", id, channel],
  queryFn: () => readJson(rpc.api["message-templates"][":id"].preview.$get({ param: { id }, query: { channel } })),
  staleTime: 0,
});

export const dripSequencesQuery = queryOptions({
  queryKey: ["drips"],
  queryFn: () => readJson(rpc.api.drips.$get()).then(result => result.sequences),
  staleTime: 60_000,
});

export const automationSettingsQuery = queryOptions({
  queryKey: ["settings-automation"],
  queryFn: () => readJson(rpc.api.settings.automation.$get()),
  staleTime: 60_000,
});

export const calendarConnectionQuery = queryOptions({
  queryKey: ["settings-calendar"],
  queryFn: () => readJson(rpc.api.settings.calendar.$get()),
  staleTime: 60_000,
});

export const bookingVendorsQuery = (bookingId: string) => queryOptions({
  queryKey: ["booking-vendors", bookingId],
  queryFn: () => readJson(rpc.api.bookings[":id"].vendors.$get({ param: { id: bookingId } })).then(result => result.items),
  staleTime: 30_000,
});

// ---------------------------------------------------------------- writes

const refreshQueue = async () => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["queue"] }),
    queryClient.invalidateQueries({ queryKey: ["queue-count"] }),
    queryClient.invalidateQueries({ queryKey: ["today"] }),
  ]);
};

export const queueApi = {
  release: async (ids: string[]) => {
    const result = await readJson(rpc.api.queue.release.$post({ json: { ids } }));
    await refreshQueue();
    return result;
  },
  // The Worker releases whatever this query matches, so the filters the list was
  // showing have to travel with the request. Sending none of them means "the
  // whole queue", which is not what a button labelled "all shown" may do.
  releaseAll: async (filters: QueueFilters = {}) => {
    const result = await readJson(rpc.api.queue["release-all"].$post({ query: filters }));
    await refreshQueue();
    return result;
  },
  cancel: async (ids: string[]) => {
    const result = await readJson(rpc.api.queue.cancel.$post({ json: { ids } }));
    await refreshQueue();
    return result;
  },
  edit: async (id: string, patch: JsonBody<typeof rpc.api.queue[":id"]["$patch"]>) => {
    const result = await readJson(rpc.api.queue[":id"].$patch({ param: { id }, json: patch }));
    await refreshQueue();
    return result.item;
  },
};

export const templateApi = {
  create: async (input: JsonBody<typeof rpc.api["message-templates"]["$post"]>) => {
    const result = await readJson(rpc.api["message-templates"].$post({ json: input }));
    await queryClient.invalidateQueries({ queryKey: ["message-templates"] });
    return result.item;
  },
  update: async (id: string, patch: JsonBody<typeof rpc.api["message-templates"][":id"]["$patch"]>) => {
    const result = await readJson(rpc.api["message-templates"][":id"].$patch({ param: { id }, json: patch }));
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["message-templates"] }),
      queryClient.invalidateQueries({ queryKey: ["message-template-preview", id] }),
    ]);
    return result.item;
  },
  remove: async (id: string) => {
    await readJson(rpc.api["message-templates"][":id"].$delete({ param: { id } }));
    await queryClient.invalidateQueries({ queryKey: ["message-templates"] });
  },
};

export const dripApi = {
  setActive: async (key: string, active: boolean) => {
    const result = await readJson(rpc.api.drips[":key"].active.$post({ param: { key }, json: { active } }));
    await queryClient.invalidateQueries({ queryKey: ["drips"] });
    return result.sequences;
  },
};

export const automationApi = {
  save: async (patch: JsonBody<typeof rpc.api.settings.automation.$patch>) => {
    const result = await readJson(rpc.api.settings.automation.$patch({ json: patch }));
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["settings-automation"] }),
      queryClient.invalidateQueries({ queryKey: ["settings"] }),
      refreshQueue(),
    ]);
    return result;
  },
};

export const calendarApi = {
  setup: async () => {
    const result = await readJson(rpc.api.settings.calendar.setup.$post());
    await queryClient.invalidateQueries({ queryKey: ["settings-calendar"] });
    return result;
  },
  share: async () => {
    const result = await readJson(rpc.api.settings.calendar.share.$post());
    await queryClient.invalidateQueries({ queryKey: ["settings-calendar"] });
    return result;
  },
  connect: async (googleCalendarId: string | null) => {
    const result = await readJson(rpc.api.settings.calendar.connect.$post({ json: { googleCalendarId } }));
    await queryClient.invalidateQueries({ queryKey: ["settings-calendar"] });
    return result;
  },
  test: async () => readJson(rpc.api.settings.calendar.test.$post()),
};

export const vendorApi = {
  add: async (bookingId: string, input: JsonBody<typeof rpc.api.bookings[":id"]["vendors"]["$post"]>) => {
    const result = await readJson(rpc.api.bookings[":id"].vendors.$post({ param: { id: bookingId }, json: input }));
    await queryClient.invalidateQueries({ queryKey: ["booking-vendors", bookingId] });
    return result.item;
  },
  update: async (bookingId: string, vendorId: string, patch: JsonBody<typeof rpc.api.bookings[":id"]["vendors"][":vendorId"]["$patch"]>) => {
    const result = await readJson(rpc.api.bookings[":id"].vendors[":vendorId"].$patch({ param: { id: bookingId, vendorId }, json: patch }));
    await queryClient.invalidateQueries({ queryKey: ["booking-vendors", bookingId] });
    return result.item;
  },
  remove: async (bookingId: string, vendorId: string) => {
    await readJson(rpc.api.bookings[":id"].vendors[":vendorId"].$delete({ param: { id: bookingId, vendorId } }));
    await queryClient.invalidateQueries({ queryKey: ["booking-vendors", bookingId] });
  },
};

/** Staff uploading a certificate on the client's behalf. Multipart, because the
 * file comes straight off a phone camera. */
export async function uploadCertificate(bookingId: string, file: File) {
  const result = await readJson(rpc.api.bookings[":id"].insurance.certificate.$post({ param: { id: bookingId }, form: { file } }));
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["booking", bookingId] }),
    queryClient.invalidateQueries({ queryKey: ["review", bookingId] }),
    queryClient.invalidateQueries({ queryKey: ["attachments", bookingId] }),
  ]);
  return result.booking;
}
